import React from "react";
import { Disclosure, Transition } from "@headlessui/react";
import { MinusCircleIcon, PlusCircleIcon } from "@heroicons/react/24/outline";
import { Trans, useTranslation } from "react-i18next";

interface AccordionItemProps {
	index?: number;
	title: string;
	text: string;
}

const AccordionItem = ({ title, text, index }: AccordionItemProps) => {
  return (
    <div className="w-full py-8 border-t last:border-b">
      <Disclosure defaultOpen={index === 0}>
        {({ open }) => (
          <div>
            <Disclosure.Button className="px-4 flex items-center gap-x-8">
              <span className="max-w-xs text-allsafe-blue text-left font-Roboto w-[840px] md:max-w-none">
                <Trans>{title}</Trans>
              </span>
              {open ? (
                <div className="hidden text-allsafe-blue items-center justify-end gap-x-2 lg:flex">
                  <span className="text-base leading-4 font-Roboto">
                    <Trans>WENIGER</Trans>
                  </span>
                  <MinusCircleIcon className="w-6 h-6" />
                </div>
              ) : (
                <div className="hidden text-allsafe-blue items-center justify-end gap-x-2 lg:flex">
                  <span className="text-base leading-4 font-Roboto">
                    <Trans>MEHR</Trans>
                  </span>
                  <PlusCircleIcon className="w-6 h-6 text-allsafe-blue" />
                </div>
              )}
            </Disclosure.Button>
            <Transition
              enter="transition duration-300 ease-out"
              enterFrom="transform scale-95 opacity-0"
              enterTo="transform scale-100 opacity-100"
              leave="transition duration-300 ease-out"
              leaveFrom="transform scale-100 opacity-100"
              leaveTo="transform scale-95 opacity-0"
            >
              <Disclosure.Panel className="max-w-3xl px-4 py-4 text-allsafe-black-400 font-Roboto font-light text-left lg:pb-0">
                <Trans>{text}</Trans>
              </Disclosure.Panel>
            </Transition>
            <Disclosure.Button className="w-full flex justify-end items-center gap-x-8 pr-10 lg:hidden">
              {open ? (
                <div className="flex text-allsafe-blue items-center gap-x-2">
                  <span className="text-base leading-4 font-Roboto">
                    <Trans>WENIGER</Trans>
                  </span>
                  <MinusCircleIcon className="w-6 h-6" />
                </div>
              ) : (
                <div className="flex text-allsafe-blue items-center gap-x-2 pt-4">
                  <span className="text-base leading-4">
                    <Trans>MEHR</Trans>
                  </span>
                  <PlusCircleIcon className="w-6 h-6 text-allsafe-blue" />
                </div>
              )}
            </Disclosure.Button>
          </div>
        )}
      </Disclosure>
    </div>
  );
};

const FaqsSection: React.FC = () => {
  const { t } = useTranslation();

  return (
    <div className="mx-8 max-w-screen-lg lg:mx-auto lg:mt-40">
      <h1 className="text-3xl leading-10 text-allsafe-blue font-Roboto mb-10 mt-14 lg:text-5xl">
        {t("Häufig gestellte Fragen")}
      </h1>
      <div>
        <AccordionItem
          index={0}
          title={"Warum ist Aluminium aus China erheblich Preisgünstiger?"}
          text={
            "Die Herstellung des Aluminiums in China erfolgt über den Einsatz von fossilen Energien, die zu wesentlich höheren CO2 Emissionen führen. 1 kg Aluminium aus China setzt bei der Produktion 20 kg CO2 frei. Energie und Lohnkosten sind günstiger, tragen aber nicht zu einer nachhaltigen Lieferkette bei. Das erschwert das erfüllen der neuen EU-Richtlinien, die auch ihre Kunden betreffen."
          }
        />
        <AccordionItem
          title={"Warum ist das Angebot an CO2 reduziertem Aluminium begrenzt?"}
          text={
            "Weil in Norwegen nur eine begrenzte Menge hergestellt werden kann, entsprechend der Produktionskapazität von Hydro. Aus diesem Grund hat allsafe sich frühzeitig Kapazitäten gesichert."
          }
        />
        <AccordionItem
          title={"Ist das Material zertifiziert"}
          text={
            "Ja, das Aluminium von Hydro ist verifiziert von DNV-GL nach ISO 14064 und durch Umwelt-Produktdeklarationen bestätigt. Hydro ist außerdem Zertifiziert von der Aluminium Stewardship Initiative."
          }
        />
        <AccordionItem
          title={"Worin liegt ihr Mehrwert?"}
          text={
            "Durch REDUXA erreichen Sie ihre Klimaziele entsprechend des ESG Reportings. Dem Lieferkettenpflichtgesetz werden Sie durch die Rückverfolgbarkeit jeder einzelnen Charge gerecht. Mit allsafe Produkten die dieses Aluminium verwenden, reduzieren Sie Ihren CO2 Ausstoß im Scope 3."
          }
        />
      </div>
    </div>
  );
};

export default FaqsSection;
